import { Link } from 'react-router-dom'

const footerLinks = {
  product: [
    { label: 'Features', href: '/features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Demo', href: '/demo' },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'For Investment Managers', href: '/investment-managers' },
    { label: 'Partner Programme', href: '/partner-programme' },
    { label: 'Contact', href: '/contact' },
  ],
  resources: [
    { label: 'Resources', href: '/resources' },
    { label: 'Value of Advice', href: '/value-of-advice' },
    { label: 'Help Us Build', href: '/help-us-build' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Security', href: '/security' },
  ],
}

export default function Footer() {
  return (
    <footer className="w-full bg-black/30 border-t border-white/[0.06] pt-20 pb-10 px-6 relative z-[1]">
      <div className="max-w-[1200px] mx-auto">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_2fr] gap-20 pb-15 border-b border-white/[0.06]">
          {/* Brand Section */}
          <div>
            <Link to="/" className="flex items-center gap-3 no-underline">
              <svg className="w-8 h-8" viewBox="0 0 64 64" fill="none">
                <defs>
                  <linearGradient id="footerEggLight" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#5eead4" />
                    <stop offset="100%" stopColor="#14b8a6" />
                  </linearGradient>
                  <linearGradient id="footerEggMid" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#14b8a6" />
                    <stop offset="100%" stopColor="#0d9488" />
                  </linearGradient>
                  <linearGradient id="footerEggDark" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#0d9488" />
                    <stop offset="100%" stopColor="#0f766e" />
                  </linearGradient>
                </defs>
                {/* Main egg/droplet shape */}
                <path
                  d="M32 4 C18 4 8 20 8 36 C8 52 18 60 32 60 C46 60 56 52 56 36 C56 20 46 4 32 4Z"
                  fill="url(#footerEggLight)"
                />
                {/* Wave layers */}
                <path
                  d="M12 38 Q22 32 32 38 Q42 44 52 38 L52 48 Q42 54 32 48 Q22 54 12 48 Z"
                  fill="url(#footerEggMid)"
                  opacity="0.9"
                />
                <path
                  d="M14 46 Q24 40 32 46 Q40 52 50 46 L50 54 Q40 60 32 54 Q24 60 14 54 Z"
                  fill="url(#footerEggDark)"
                  opacity="0.85"
                />
                {/* Gold accent curves */}
                <path
                  d="M20 24 Q26 20 32 24"
                  stroke="#B8956A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                  opacity="0.7"
                />
                <path
                  d="M36 20 Q40 18 44 20"
                  stroke="#B8956A"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                  opacity="0.5"
                />
              </svg>
              <div className="font-serif text-[22px] font-medium tracking-tight text-brand-light">
                Wealth<span className="text-accent-tealLight">Space</span>
              </div>
            </Link>
            <p className="text-[15px] text-brand-subtle leading-relaxed mt-5 max-w-[300px]">
              AI-powered software that transforms how financial advisers work, every single day.
            </p>
            <p className="text-[13px] text-accent-teal font-medium mt-3 italic">
              Built by Advisers. Run by Advisers. Owned by Advisers.
            </p>
          </div>

          {/* Links Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FooterColumn title="Product" links={footerLinks.product} />
            <FooterColumn title="Company" links={footerLinks.company} />
            <FooterColumn title="Resources" links={footerLinks.resources} />
            <FooterColumn title="Legal" links={footerLinks.legal} />
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row justify-between items-center pt-[30px] gap-5">
          <p className="text-[13px] text-brand-subtle text-center sm:text-left">
            &copy; {new Date().getFullYear()} WealthSpace Ltd (Company No. 15396707). Registered in
            England &amp; Wales.
          </p>
        </div>
      </div>
    </footer>
  )
}

interface FooterColumnProps {
  title: string
  links: { label: string; href: string }[]
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div>
      <h4 className="text-[13px] font-semibold text-brand-muted uppercase tracking-[0.05em] mb-5">
        {title}
      </h4>
      {links.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className="block text-sm text-brand-subtle py-1.5 no-underline transition-colors duration-300 hover:text-brand-light"
        >
          {link.label}
        </Link>
      ))}
    </div>
  )
}
